// A popover anchored to its button (Console D's .pop: the scope, the chips, the account menu):
// a disclosure, so its button says whether it is open, and a click outside, Esc or a route change
// closes it. It sits in a fixed layer, below the button or above it (the account menu, at the
// foot of the sidebar), kept inside the window. The app passes its current path, so the package
// needs no router.
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

export function usePopover(path: string, placement: 'below' | 'above' = 'below') {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  const pop = useRef<HTMLDivElement>(null)
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden' })
  const close = useCallback((focusBack = false) => {
    setOpen(false)
    if (focusBack) anchor.current?.focus()
  }, [])
  const toggle = useCallback(() => setOpen((o) => !o), [])
  // A new page closes it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes on every path change
  useEffect(() => setOpen(false), [path])

  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const a = anchor.current?.getBoundingClientRect()
      const p = pop.current
      if (!a || !p) return
      const w = p.offsetWidth
      const left = Math.max(8, Math.min(a.left, window.innerWidth - w - 8))
      const top = placement === 'below' ? a.bottom + 6 : Math.max(8, a.top - p.offsetHeight - 8)
      setStyle({ left, top })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [open, placement])

  useEffect(() => {
    if (!open) {
      setStyle({ visibility: 'hidden' })
      return
    }
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (pop.current?.contains(t) || anchor.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        close(true)
      }
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, close])

  return { open, toggle, close, anchor, pop, style }
}
